// Decision logic of the data synchronisation (see sync.js), kept free of any
// DOM or network access. Every decision derives from wall-clock timestamps of
// the last *successful* loads, never from timers having run: browsers delay,
// throttle or freeze timers (hidden tab, sleeping device), so elapsed time is
// the only reliable signal that something was missed.

export const SYNC = {
  // heartbeat: cheap clock check, catches resumes that fire no event at all
  // (e.g. computer waking from sleep with the page still in front)
  TICK_MS: 5000,
  // live measures refresh period while the page is visible
  LIVE_PERIOD_MS: 30000,
  // sensors report every few minutes and the timeline keeps one measure per
  // sensor and 10 min step: while live loads stay less than 5 min apart, every
  // step gets filled. Beyond that, steps may have been missed: reload history.
  HISTORY_GAP_MS: 5 * 60000,
  // past this delay without live load, the user was away: back to the present
  RESUME_GAP_MS: 15 * 60000,
  // safety net: full history reload while the page stays visible
  HISTORY_MAX_AGE_MS: 60 * 60000,
  RETRY_DELAYS_MS: [5000, 15000, 30000, 60000]
};

export function retryDelay(failures) {
  return SYNC.RETRY_DELAYS_MS[Math.min(failures, SYNC.RETRY_DELAYS_MS.length - 1)];
}

/**
 * @param state {frameDay, lastHistorySync, lastLiveSync, historyNeeded, nextAttemptAt}
 *        timestamps in ms, 0 when never loaded
 * @param ctx {now, today, visible, editMode, followLive, periodic, forceFull, forceAlign}
 * @returns {{kind: null|'live'|'full', skip: ?string, followLive: boolean,
 *            rebuildFrame: boolean, align: boolean}}
 *   kind: 'live' = latest measures only, 'full' = history + latest measures
 *   rebuildFrame: the timeline frame was built on a previous day
 *   align: move the cursor onto the live head even if the head did not change
 */
export function planSync(state, ctx) {
  const forced = ctx.forceFull || ctx.forceAlign;
  if (!ctx.visible && !forced) {
    // nobody is looking: catch up as soon as the page is visible again
    return {kind: null, skip: 'hidden'};
  }
  if (ctx.periodic && ctx.now < state.nextAttemptAt) {
    return {kind: null, skip: 'backoff'};
  }

  const sinceLive = ctx.now - state.lastLiveSync;
  const resumed = state.lastLiveSync > 0 && sinceLive > SYNC.RESUME_GAP_MS;
  const followLive = ctx.followLive || resumed;
  // never move the cursor or reshape the timeline while a spot is being edited
  const canMoveCursor = !ctx.editMode;
  // the frame is only shifted under a user following the present, not under
  // someone browsing past or forecast steps
  const rebuildFrame = canMoveCursor && followLive && state.frameDay !== null && state.frameDay !== ctx.today;
  const needsHistory = state.historyNeeded || !state.lastHistorySync ||
    sinceLive > SYNC.HISTORY_GAP_MS || ctx.now - state.lastHistorySync > SYNC.HISTORY_MAX_AGE_MS;

  const kind = ctx.forceFull || rebuildFrame || needsHistory ? 'full' : 'live';
  if (kind === 'live' && !forced && sinceLive < SYNC.LIVE_PERIOD_MS) {
    return {kind: null, skip: 'fresh'};
  }
  return {
    kind,
    skip: null,
    followLive,
    rebuildFrame,
    align: canMoveCursor && (ctx.forceAlign || resumed || rebuildFrame)
  };
}
